import React, { useEffect, useState } from 'react';

import {
  Typography,
  Box,
  Divider,
  Backdrop,
  IconButton,
  CircularProgress,
} from '@mui/material';

import { ReactComponent as ChevronRightDarkBlue } from '../../../../common/assets/icons/chevron_right_dark_blue.svg';
import { SelectableCode } from '../../../../common/types/Codes';
import { SaveSheet } from '../../../../common/types/SaveSheet';
import AddCodeContainer from '../../AddCodeContainer/AddCodeContainer';
import UseAllButton from '../components/UseAllButton';

import CheckCodesButton from './CheckCodesButton';
import CodeComponent from './CodeComponent';
import LookupCodeButton from './LookupCodeButton';

type Props = {
  allCodes: SelectableCode[];
  selectedCodes: SelectableCode[];
  note: SaveSheet | null;
  selectCode: (code: SelectableCode) => void;
  selectedAll: (select: boolean) => void;
  selectMultiplier: (code: SelectableCode, multiplier: number) => void;
  selectModifiers: (code: SelectableCode, modifiers: string[]) => void;
  addManualCode: (code: SelectableCode) => void;
  flagCode: (code: SelectableCode) => void;
  isLoading: boolean;
  hasSelectedAll: boolean;
  onSaveNote: () => void;
  saveIsLoading: boolean;
};

const CodesSection: React.FC<Props> = ({
  allCodes,
  selectedCodes,
  note,
  selectCode,
  selectedAll,
  selectMultiplier,
  selectModifiers,
  addManualCode,
  flagCode,
  isLoading,
  hasSelectedAll,
  onSaveNote,
  saveIsLoading,
}) => {
  const [cptCodes, setCptCodes] = useState<SelectableCode[]>([]);
  const [hcpcsCodes, setHcpcsCodes] = useState<SelectableCode[]>([]);
  const [icd10Codes, setICD10Codes] = useState<SelectableCode[]>([]);

  const [expandCPT, setExpandCPT] = useState(true);
  const toggleCPT = () => setExpandCPT(!expandCPT);
  const rotateCPT = expandCPT ? 'rotate(90deg)' : 'rotate(0)';

  const [expandHCPCS, setExpandHCPCS] = useState(true);
  const toggleHCPCS = () => setExpandHCPCS(!expandHCPCS);
  const rotateHCPCS = expandHCPCS ? 'rotate(90deg)' : 'rotate(0)';

  const [expandICD10, setExpandICD10] = useState(true);
  const toggleICD10 = () => setExpandICD10(!expandICD10);
  const rotateICD10 = expandICD10 ? 'rotate(90deg)' : 'rotate(0)';

  const [addCodePopup, setAddCodePopup] = useState<
    'CPT' | 'HCPCS' | 'ICD10' | undefined
  >(undefined);

  useEffect(() => {
    // extract CPT codes
    const newCptCodes = extractCPTCodes(allCodes);
    setCptCodes(newCptCodes);

    // from the remained codes, extract HCPCS
    // get remained codes
    let remainedCodes = allCodes.filter(
      (result) => !newCptCodes.includes(result)
    );
    const newHCPCS = extractHCPCSCodes(remainedCodes);
    setHcpcsCodes(newHCPCS);

    // from the remained codes, extract ICD10
    remainedCodes = remainedCodes.filter(
      (result) => !newHCPCS.includes(result)
    );
    setICD10Codes(extractICD10Codes(remainedCodes));
    remainedCodes = remainedCodes.filter(
      (result) => !extractICD10Codes(remainedCodes).includes(result)
    );
  }, [allCodes]);

  const extractCPTCodes = (codes: SelectableCode[]) => {
    const cptRegex = /^\d{5}$/;

    return codes.filter((result) => cptRegex.test(result.code));
  };

  const extractHCPCSCodes = (codes: SelectableCode[]) => {
    const hcpcsRegex = /^[A-V0-9]{1,5}$/;
    return codes.filter((result) => hcpcsRegex.test(result.code));
  };

  const extractICD10Codes = (codes: SelectableCode[]) => {
    const icd10Regex = /^[A-TV-Z][0-9][A-Z0-9]([A-Z0-9]|\.[A-Z0-9]{1,4})?$/;
    return codes.filter((result) => icd10Regex.test(result.code));
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#D8F4F8',
          width: '100%',
          borderRadius: '16px',
          padding: '16px',
          marginTop: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton
              style={{
                transform: rotateCPT,
                transition: 'all 0.25s ease-in-out',
                alignSelf: 'flex-start',
              }}
              onClick={() => toggleCPT()}
            >
              <ChevronRightDarkBlue
                style={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </IconButton>
            {isLoading ? (
              <CircularProgress
                size='20px'
                sx={{
                  color: 'primary',
                }}
              />
            ) : (
              <Typography color='darkBlue' fontWeight={700} fontSize={20}>
                {cptCodes?.length ?? 0} CPT codes found
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <UseAllButton
              isDisabled={isLoading}
              onClick={selectedAll}
              hasSelectedAll={hasSelectedAll}
            />
            <LookupCodeButton
              isDisabled={isLoading}
              onClick={() => setAddCodePopup('CPT')}
            />
            <CheckCodesButton
              isDisabled={isLoading}
              isLoading={saveIsLoading}
              onClick={onSaveNote}
            />
          </Box>
        </Box>
        {expandCPT && (
          <>
            <Divider style={{ marginTop: '16px', marginBottom: '16px' }} />

            <Box
              sx={{
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {cptCodes.map((code) => (
                <CodeComponent
                  key={code.code}
                  code={code}
                  selectCode={selectCode}
                  selectMultiplier={selectMultiplier}
                  selectModifiers={selectModifiers}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          backgroundColor: '#D8F4F8',
          width: '100%',
          borderRadius: '16px',
          padding: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton
              style={{
                transform: rotateICD10,
                transition: 'all 0.25s ease-in-out',
                alignSelf: 'flex-start',
              }}
              onClick={() => toggleICD10()}
            >
              <ChevronRightDarkBlue
                style={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </IconButton>
            {isLoading ? (
              <CircularProgress
                size='20px'
                sx={{
                  color: 'primary',
                }}
              />
            ) : (
              <Typography color='darkBlue' fontWeight={700} fontSize={20}>
                {icd10Codes?.length ?? 0} ICD-10 codes found
              </Typography>
            )}
          </Box>
          <LookupCodeButton
            isDisabled={isLoading}
            onClick={() => setAddCodePopup('ICD10')}
          />
        </Box>
        {expandICD10 && (
          <>
            <Divider style={{ marginTop: '16px', marginBottom: '16px' }} />

            <Box
              sx={{
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {icd10Codes.map((code) => (
                <CodeComponent
                  key={code.code}
                  code={code}
                  selectCode={selectCode}
                  selectMultiplier={selectMultiplier}
                  selectModifiers={selectModifiers}
                  isICD10
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      {/*<Box*/}
      {/*  sx={{*/}
      {/*    backgroundColor: "#D8F4F8",*/}
      {/*    width: "100%",*/}
      {/*    borderRadius: "16px",*/}
      {/*    padding: "16px",*/}
      {/*  }}>*/}
      {/*  <Box*/}
      {/*    sx={{*/}
      {/*      display: "flex",*/}
      {/*      justifyContent: "space-between",*/}
      {/*      alignItems: "center",*/}
      {/*      gap: "16px",*/}
      {/*      flexWrap: "wrap",*/}
      {/*    }}>*/}
      {/*    <Box*/}
      {/*      sx={{*/}
      {/*        display: "flex",*/}
      {/*        gap: "8px",*/}
      {/*        justifyContent: "space-between",*/}
      {/*        alignItems: "center",*/}
      {/*      }}>*/}
      {/*      <IconButton*/}
      {/*        style={{*/}
      {/*          transform: rotateHCPCS,*/}
      {/*          transition: "all 0.25s ease-in-out",*/}
      {/*          alignSelf: "flex-start",*/}
      {/*        }}*/}
      {/*        onClick={() => toggleHCPCS()}>*/}
      {/*        <ChevronRightDarkBlue*/}
      {/*          style={{*/}
      {/*            width: "24px",*/}
      {/*            height: "24px",*/}
      {/*          }}*/}
      {/*        />*/}
      {/*      </IconButton>*/}
      {/*      {isLoading ? (*/}
      {/*        <CircularProgress*/}
      {/*          size="20px"*/}
      {/*          sx={{*/}
      {/*            color: "primary",*/}
      {/*          }}*/}
      {/*        />*/}
      {/*      ) : (*/}
      {/*        <Typography color="darkBlue" fontWeight={700} fontSize={20}>*/}
      {/*          {hcpcsCodes?.length ?? 0} HCPCs Level II codes found*/}
      {/*        </Typography>*/}
      {/*      )}*/}
      {/*    </Box>*/}
      {/*    <LookupCodeButton*/}
      {/*      isDisabled={isLoading}*/}
      {/*      onClick={() => setAddCodePopup("HCPCS")}*/}
      {/*    />*/}
      {/*  </Box>*/}
      {/*  {expandHCPCS && (*/}
      {/*    <>*/}
      {/*      <Divider style={{ marginTop: "16px", marginBottom: "16px" }} />*/}

      {/*      <Box*/}
      {/*        sx={{*/}
      {/*          marginTop: "16px",*/}
      {/*          display: "flex",*/}
      {/*          flexDirection: "column",*/}
      {/*          gap: "8px",*/}
      {/*        }}>*/}
      {/*        {hcpcsCodes.map((code) => (*/}
      {/*          <CodeComponent*/}
      {/*            key={code.code}*/}
      {/*            code={code}*/}
      {/*            selectCode={selectCode}*/}
      {/*            selectMultiplier={selectMultiplier}*/}
      {/*            selectModifiers={selectModifiers}*/}
      {/*          />*/}
      {/*        ))}*/}
      {/*      </Box>*/}
      {/*    </>*/}
      {/*  )}*/}
      {/*</Box>*/}

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1101,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        open={addCodePopup !== undefined}
      >
        {addCodePopup && (
          <AddCodeContainer
            isClinicNote={false}
            selectedCodes={selectedCodes ?? []}
            addManualCode={addManualCode}
            onClickClose={() => {
              setAddCodePopup(undefined);
            }}
            type={addCodePopup}
          />
        )}
      </Backdrop>
    </>
  );
};

export default CodesSection;
